import { ObjectType } from '$app-web/types';

import { areValuesEqual } from './helper';

type ArrayType<T> = Array<T> | ReadonlyArray<T>;

export function shuffleArray<T>(array: ArrayType<T>): Array<T> {
    return array.toSorted(() => Math.random() - 0.5);
}

export function getRandomElementFromArray<T>(array: ArrayType<T>): T {
    const randomIndex = Math.floor(Math.random() * array.length);

    return array[randomIndex];
}

export function getRandomElementsFromArray<T>(array: ArrayType<T>, count: number): Array<T> {
    const shuffledArray = shuffleArray(array);

    return shuffledArray.slice(0, count);
}

export function getArraysDiff<T extends string | number>(firstArray: Array<T>, secondArray: Array<T>): Array<T> {
    const dataSet = new Set([...firstArray, ...secondArray]);

    return Array.from(dataSet).filter((item) => !firstArray.includes(item) || !secondArray.includes(item));
}

export function removeArrayDuplicates<T>(array: Array<T>): Array<T> {
    const uniqueArray: Array<T> = [];

    array.forEach((item) => {
        if (uniqueArray.find((uniqueItem) => areValuesEqual(uniqueItem, item)) == null) {
            uniqueArray.push(item);
        }
    });

    return uniqueArray;
}

export function removeArrayNullishValues<T>(array: Array<T | null | undefined>): Array<T> {
    return array.filter((item) => item != null) as Array<T>;
}

export function sortArray<T>(
    array: Array<T>,
    callback: (item: T) => number | string | boolean | Date | null | undefined,
    options: { order: 'asc' | 'desc' } = { order: 'asc' }
): Array<T> {
    const { order } = options;

    return array.toSorted((firstValue, secondValue) => {
        const a = callback(firstValue);
        const b = callback(secondValue);

        let result = 0;

        if (a == null && b == null) {
            return 0;
        } else if (a == null) {
            return 1;
        } else if (b == null) {
            return -1;
        } else if (typeof a === 'string' && typeof b === 'string') {
            result = a.localeCompare(b);
        } else if (a > b) {
            result = 1;
        } else if (a < b) {
            result = -1;
        }

        return result * (order === 'asc' ? 1 : -1);
    });
}

export function resizeArrayFromEnd<T>(array: Array<T>, targetLength: number, defaultValue: T) {
    if (array.length >= targetLength) {
        return array.slice(0, targetLength);
    } else {
        const endingElements = Array.from({ length: targetLength - array.length }, () => defaultValue);

        return [...array, ...endingElements];
    }
}

export function groupArrayBy<T, Result extends ObjectType<Array<T>>>(
    data: Array<T>,
    separatorCallback: (item: T) => string | number
): Result {
    return data.reduce((newObject, item) => {
        const groupKey = separatorCallback(item);

        if (groupKey in newObject) {
            const prevValue = newObject[groupKey];

            return { ...newObject, [groupKey]: [...prevValue, item] } as Result;
        } else {
            return { ...newObject, [groupKey]: [item] } as Result;
        }
    }, {} as Result);
}
